import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { API_DOCS } from './ScontiFornitatoriPage.api';

// ── Interfacce ────────────────────────────────────────────────────────────────

interface ScontoItem {
  id: number;
  codice: string;
  descrizione: string | null;
  percentualeEquivalente: number;
  moltiplicatore: number;
}

interface ScontoFornitore {
  id: number;
  fornitoreId: number;
  societaId: number;
  societa?: { id: number; nome: string };
  fornitore?: { id: number; ragioneSociale: string | null; nome: string | null };
  vendBasso: ScontoItem | null;
  vendMedio: ScontoItem | null;
  vendAlto:  ScontoItem | null;
  vendMax:   ScontoItem | null;
  acqBasso:  ScontoItem | null;
  acqMedio:  ScontoItem | null;
  acqAlto:   ScontoItem | null;
  acqMax:    ScontoItem | null;
}

interface Fornitore {
  id: number;
  ragioneSociale: string | null;
  nome: string | null;
}

interface ArticoloRicerca {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  prezzoStandard: number | null;
  umVendita: { simbolo: string } | null;
  marca: { nome: string } | null;
}

const TIPOLOGIE_LABEL: Record<string, string> = {
  PRODOTTO_CONFIGURATO: 'Prodotto Configurato',
  ARTICOLO_FISICO: 'Articolo Fisico',
  LAVORAZIONE_PRESTAZIONE: 'Lavorazione/Prestazione',
  LICENZA: 'Licenza',
  CANONE: 'Canone',
  INTERCAMBIABILE: 'Intercambiabile',
  RAGGRUPPAMENTO_PREZZO_CORPO: 'Raggrupp. articoli - prezzo a corpo',
};

// ── Componente principale ─────────────────────────────────────────────────────

export default function ScontiFornitatoriPage() {
  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Lista tabella
  const [lista,       setLista]       = useState<ScontoFornitore[]>([]);
  const [isLoading,   setIsLoading]   = useState(true);
  const [highlightId, setHighlightId] = useState<number | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Dati per le select
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [sconti,    setSconti]    = useState<ScontoItem[]>([]);

  // Modale sconti (creazione/modifica)
  const [showModal,   setShowModal]   = useState(false);
  const [editSf,      setEditSf]      = useState<ScontoFornitore | null>(null);
  const [modalFornId, setModalFornId] = useState('');
  const [cercaForn,   setCercaForn]   = useState('');
  const [form, setForm] = useState({
    vendBassoId: '', vendMedioId: '', vendAltoId: '', vendMaxId: '',
    acqBassoId:  '', acqMedioId:  '', acqAltoId:  '', acqMaxId:  '',
  });
  const [saving,   setSaving]   = useState(false);
  const [errModal, setErrModal] = useState('');

  // Simulatore
  const [simFornitoreId, setSimFornitoreId] = useState('');
  const [simSconti,      setSimSconti]      = useState<ScontoFornitore | null>(null);
  const [articoloSel,    setArticoloSel]    = useState<ArticoloRicerca | null>(null);

  // Modale articolo
  const [showModalArticolo, setShowModalArticolo] = useState(false);
  const [modalQ,            setModalQ]            = useState('');
  const [modalTipologia,    setModalTipologia]    = useState('');
  const [articoliModal,     setArticoliModal]     = useState<ArticoloRicerca[]>([]);
  const [loadingModal,      setLoadingModal]      = useState(false);

  // ── Helper toast ──────────────────────────────────────────────────────────
  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Caricamento lista ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!pronta) return;
    fetchLista();
  }, [pronta, filterSocietaId]); // eslint-disable-line

  async function fetchLista() {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSocietaId) params.set('societaId', filterSocietaId);
      // @api GET /api/anagrafiche/sconti-fornitore?societaId=N -> {"success":true,"data":"ScontoFornitore[]"}
      const res = await apiFetch<{ success: boolean; data: ScontoFornitore[] }>(
        `/api/anagrafiche/sconti-fornitore?${params}`
      );
      setLista(res.data ?? []);
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  // ── Caricamento fornitori e sconti per le select ───────────────────────────
  useEffect(() => {
    if (!pronta) return;
    Promise.all([
      // @api GET /api/nominativi?ruolo=FORNITORE&limit=200&societaId=N -> {"data":"Nominativo[]"}
      apiFetch<{ data: Fornitore[] }>(
        `/api/nominativi?ruolo=FORNITORE&limit=200${filterSocietaId ? `&societaId=${filterSocietaId}` : ''}`
      ).then(r => setFornitori(r.data ?? [])),
      // @api GET /api/sconti?societaId=N&mostraDisattivati=false -> "ScontoItem[]"
      apiFetch<ScontoItem[]>(
        `/api/sconti?${filterSocietaId ? `societaId=${filterSocietaId}&` : ''}mostraDisattivati=false`
      ).then(r => setSconti(Array.isArray(r) ? r : [])),
    ]).catch(console.error);
  }, [pronta, filterSocietaId]); // eslint-disable-line

  // ── Simulatore: carica sconti del fornitore selezionato ───────────────────
  useEffect(() => {
    if (!simFornitoreId || !filterSocietaId) {
      setSimSconti(null);
      setArticoloSel(null);
      return;
    }
    // @api GET /api/anagrafiche/sconti-fornitore/:fornitoreId?societaId=N -> {"success":true,"data":"ScontoFornitore|null"}
    apiFetch<{ success: boolean; data: ScontoFornitore | null }>(
      `/api/anagrafiche/sconti-fornitore/${simFornitoreId}?societaId=${filterSocietaId}`
    ).then(res => setSimSconti(res.data))
     .catch(console.error);
  }, [simFornitoreId, filterSocietaId]); // eslint-disable-line

  // ── Modale articolo: caricamento immediato all'apertura ───────────────────
  useEffect(() => {
    if (!showModalArticolo || !filterSocietaId) return;
    cercaArticoliModal('', '');
  }, [showModalArticolo]); // eslint-disable-line

  // ── Modale articolo: caricamento con debounce al cambio filtri ────────────
  useEffect(() => {
    if (!showModalArticolo || !filterSocietaId) return;
    const timer = setTimeout(() => {
      cercaArticoliModal(modalQ, modalTipologia);
    }, 300);
    return () => clearTimeout(timer);
  }, [modalQ, modalTipologia]); // eslint-disable-line

  async function cercaArticoliModal(q: string, tipologia: string) {
    setLoadingModal(true);
    try {
      const params = new URLSearchParams({
        societaId: String(filterSocietaId),
        limit: '30', page: '1',
      });
      if (q.trim())   params.set('q', q.trim());
      if (tipologia)  params.set('tipologia', tipologia);
      // @api GET /api/anagrafiche/articoli?societaId=N&q=&tipologia=&page=1&limit=30 -> {"success":true,"data":"ArticoloRicerca[]","total":"number"}
      const res = await apiFetch<{ success: boolean; data: ArticoloRicerca[] }>(
        `/api/anagrafiche/articoli?${params}`
      );
      setArticoliModal(res.data ?? []);
    } catch (err) {
      console.error('[ScontiFornitori] cercaArticoliModal', err);
    } finally {
      setLoadingModal(false);
    }
  }

  // ── Apertura modali ───────────────────────────────────────────────────────
  function openModal() {
    // Blocca se nessuna società selezionata — obbligatorio per il multisocietà
    if (!filterSocietaId) {
      showToast('Seleziona prima una società dalla barra in alto prima di aggiungere sconti fornitore.', 'error');
      return;
    }
    setEditSf(null);
    setModalFornId('');
    setCercaForn('');
    setForm({ vendBassoId: '', vendMedioId: '', vendAltoId: '', vendMaxId: '',
              acqBassoId:  '', acqMedioId:  '', acqAltoId:  '', acqMaxId:  '' });
    setErrModal('');
    setShowModal(true);
  }

  function openModifica(sf: ScontoFornitore) {
    setEditSf(sf);
    setModalFornId(String(sf.fornitoreId));
    setCercaForn('');
    setForm({
      vendBassoId: sf.vendBasso?.id ? String(sf.vendBasso.id) : '',
      vendMedioId: sf.vendMedio?.id ? String(sf.vendMedio.id) : '',
      vendAltoId:  sf.vendAlto?.id  ? String(sf.vendAlto.id)  : '',
      vendMaxId:   sf.vendMax?.id   ? String(sf.vendMax.id)   : '',
      acqBassoId:  sf.acqBasso?.id  ? String(sf.acqBasso.id)  : '',
      acqMedioId:  sf.acqMedio?.id  ? String(sf.acqMedio.id)  : '',
      acqAltoId:   sf.acqAlto?.id   ? String(sf.acqAlto.id)   : '',
      acqMaxId:    sf.acqMax?.id    ? String(sf.acqMax.id)    : '',
    });
    setErrModal('');
    setShowModal(true);
  }

  function apriModalArticolo() {
    setModalQ('');
    setModalTipologia('');
    setArticoliModal([]);
    setShowModalArticolo(true);
  }

  // ── Salvataggio dalla modale ──────────────────────────────────────────────
  async function handleSalva() {
    if (!modalFornId) { setErrModal('Seleziona un fornitore'); return; }
    const sid = filterSocietaId || (listaSocieta.length === 1 ? String(listaSocieta[0].id) : '');
    if (!sid) { setErrModal('Seleziona una società'); return; }
    setSaving(true);
    setErrModal('');
    try {
      // @api PUT /api/anagrafiche/sconti-fornitore/:fornitoreId {"societaId":"number","vendBassoId":"number|null","vendMedioId":"number|null","vendAltoId":"number|null","vendMaxId":"number|null","acqBassoId":"number|null","acqMedioId":"number|null","acqAltoId":"number|null","acqMaxId":"number|null"} -> {"success":true,"data":"ScontoFornitore"}
      const res = await apiFetch<{ success: boolean; data: ScontoFornitore }>(
        `/api/anagrafiche/sconti-fornitore/${modalFornId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            societaId:   parseInt(sid),
            vendBassoId: form.vendBassoId ? parseInt(form.vendBassoId) : null,
            vendMedioId: form.vendMedioId ? parseInt(form.vendMedioId) : null,
            vendAltoId:  form.vendAltoId  ? parseInt(form.vendAltoId)  : null,
            vendMaxId:   form.vendMaxId   ? parseInt(form.vendMaxId)   : null,
            acqBassoId:  form.acqBassoId  ? parseInt(form.acqBassoId)  : null,
            acqMedioId:  form.acqMedioId  ? parseInt(form.acqMedioId)  : null,
            acqAltoId:   form.acqAltoId   ? parseInt(form.acqAltoId)   : null,
            acqMaxId:    form.acqMaxId    ? parseInt(form.acqMaxId)    : null,
          }),
        }
      );
      setShowModal(false);
      showToast(editSf ? 'Sconti aggiornati' : 'Sconti fornitore creati');
      const newSf = res.data;
      setHighlightId(newSf.id);
      setTimeout(() => setHighlightId(null), 2000);
      await fetchLista();
    } catch (err: any) {
      setErrModal(err.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  // ── Fornitori filtrati per la modale creazione ────────────────────────────
  const fornitoriFiltrati = fornitori.filter(f => {
    const nome = f.ragioneSociale || f.nome || '';
    return nome.toLowerCase().includes(cercaForn.toLowerCase());
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="sconti-forn-page">

        {/* Toast */}
        {toast && (
          <div className={`amministrazione-toast ${
            toast.type === 'success'
              ? 'amministrazione-toast-success'
              : 'amministrazione-toast-error'
          }`}>
            {toast.msg}
          </div>
        )}

        {/* Header */}
        <div className="page-header">
          <div className="page-header-text">
            <h2>Sconti Fornitori</h2>
            <p>Gestione sconti su vendite e acquisti per fornitore</p>
          </div>
          <button className="bop-btn-primary" onClick={openModal}>
            + Nuovi Sconti Fornitore
          </button>
        </div>

        {/* Filtro società */}
        <div className="bop-card amministrazione-filter-card">
          <div className="amministrazione-filter-wrapper">
            {!isUnica && (
              <select
                value={filterSocietaId}
                onChange={e => setFilterSocietaId(e.target.value)}
                className="bop-select amministrazione-filter-select"
              >
                <option value="">Tutte le società</option>
                {listaSocieta.map(s => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Tabella lista */}
        <div className="bop-card">
          {isLoading ? (
            <div className="amministrazione-loading">Caricamento...</div>
          ) : lista.length === 0 ? (
            <div className="amministrazione-empty">
              <p className="amministrazione-empty-text">Nessuno sconto fornitore configurato</p>
            </div>
          ) : (
            <div className="amministrazione-table-scroll">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Fornitore</th>
                    {!isUnica && <th>Società</th>}
                    <th>Vend. Basso</th>
                    <th>Vend. Medio</th>
                    <th>Vend. Alto</th>
                    <th>Vend. Max</th>
                    <th>Acq. Basso</th>
                    <th>Acq. Medio</th>
                    <th>Acq. Alto</th>
                    <th>Acq. Max</th>
                    <th className="bop-text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map(sf => (
                    <tr key={sf.id} className={sf.id === highlightId ? 'amministrazione-table-row-highlight' : ''}>
                      <td>
                        <span className="amministrazione-banca-nome">
                          {sf.fornitore?.ragioneSociale || sf.fornitore?.nome || '-'}
                        </span>
                      </td>
                      {!isUnica && <td className="bop-text-muted">{sf.societa?.nome}</td>}
                      <td className="bop-text-muted">{sf.vendBasso?.codice || '—'}</td>
                      <td className="bop-text-muted">{sf.vendMedio?.codice || '—'}</td>
                      <td className="bop-text-muted">{sf.vendAlto?.codice  || '—'}</td>
                      <td className="bop-text-muted">{sf.vendMax?.codice   || '—'}</td>
                      <td className="bop-text-muted">{sf.acqBasso?.codice  || '—'}</td>
                      <td className="bop-text-muted">{sf.acqMedio?.codice  || '—'}</td>
                      <td className="bop-text-muted">{sf.acqAlto?.codice   || '—'}</td>
                      <td className="bop-text-muted">{sf.acqMax?.codice    || '—'}</td>
                      <td>
                        <div className="amministrazione-actions-wrapper">
                          <button
                            className="bop-btn-secondary amministrazione-btn-small"
                            onClick={() => openModifica(sf)}
                          >
                            Modifica
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Simulatore prezzi — sempre visibile */}
        <div className="bop-card">
          <div className="sconti-forn-simulatore-header">
            <div>
              <h3 className="sconti-forn-sezione-titolo">Simulatore prezzi</h3>
              <p className="bop-text-muted">
                Seleziona un fornitore e cerca un articolo per simulare i prezzi con gli sconti applicati.
              </p>
            </div>
          </div>

          {/* Selezione fornitore */}
          <div className="bop-form-group sconti-forn-sim-fornitore">
            <label className="bop-label">Fornitore</label>
            <select
              className="bop-select"
              value={simFornitoreId}
              onChange={e => setSimFornitoreId(e.target.value)}
            >
              <option value="">— seleziona fornitore —</option>
              {fornitori.map(f => (
                <option key={f.id} value={f.id}>
                  {f.ragioneSociale || f.nome}
                </option>
              ))}
            </select>
          </div>

          {simFornitoreId && (
            <div className="sconti-forn-sim-articolo-row">
              <span className="bop-text-muted">
                {articoloSel
                  ? `${articoloSel.codice} — ${articoloSel.descrizioneBase}`
                  : 'Nessun articolo selezionato'}
              </span>
              <button className="bop-btn-secondary" onClick={apriModalArticolo}>
                🔍 Cerca articolo
              </button>
            </div>
          )}

          {articoloSel && simFornitoreId && (
            <div className="sconti-forn-simulatore">
              <div className="sconti-forn-articolo-info">
                <span className="sconti-forn-articolo-codice">{articoloSel.codice}</span>
                <span>{articoloSel.descrizioneBase}</span>
                <span className="bop-text-muted">
                  Prezzo standard:{' '}
                  {articoloSel.prezzoStandard !== null
                    ? `€ ${Number(articoloSel.prezzoStandard).toFixed(2)}`
                    : '—'}
                  {articoloSel.umVendita ? ` / ${articoloSel.umVendita.simbolo}` : ''}
                </span>
              </div>

              {articoloSel.prezzoStandard !== null ? (
                <table className="bop-table sconti-forn-tabella-prezzi">
                  <thead>
                    <tr>
                      <th>Livello</th>
                      <th>Sconto vendita</th>
                      <th>Prezzo vendita</th>
                      <th>Sconto acquisto</th>
                      <th>Prezzo acquisto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['Basso', 'Medio', 'Alto', 'Max'] as const).map(livello => {
                      const scontoVend = simSconti?.[`vend${livello}` as keyof ScontoFornitore] as ScontoItem | null;
                      const scontoAcq  = simSconti?.[`acq${livello}`  as keyof ScontoFornitore] as ScontoItem | null;
                      const prezzo     = Number(articoloSel.prezzoStandard);
                      const prezzoVend = scontoVend
                        ? Math.round(prezzo * Number(scontoVend.moltiplicatore) * 100) / 100
                        : null;
                      const prezzoAcq = scontoAcq
                        ? Math.round(prezzo * Number(scontoAcq.moltiplicatore) * 100) / 100
                        : null;
                      return (
                        <tr key={livello}>
                          <td><strong>{livello}</strong></td>
                          <td className="bop-text-muted">
                            {scontoVend
                              ? `${scontoVend.codice} (${Number(scontoVend.percentualeEquivalente).toFixed(2)}%)`
                              : '—'}
                          </td>
                          <td className="sconti-forn-prezzo">
                            {prezzoVend !== null ? `€ ${prezzoVend.toFixed(2)}` : '—'}
                          </td>
                          <td className="bop-text-muted">
                            {scontoAcq
                              ? `${scontoAcq.codice} (${Number(scontoAcq.percentualeEquivalente).toFixed(2)}%)`
                              : '—'}
                          </td>
                          <td className="sconti-forn-prezzo">
                            {prezzoAcq !== null ? `€ ${prezzoAcq.toFixed(2)}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="bop-text-muted">
                  Questo articolo non ha un prezzo standard — simulazione non disponibile.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Modale sconti (creazione e modifica) */}
        {showModal && (
          <div className="articoli-modal-overlay">
            <div className="sconti-forn-modal" onClick={e => e.stopPropagation()}>
              <div className="articoli-modal-header">
                <h3 className="articoli-modal-title">
                  {editSf ? 'Modifica Sconti Fornitore' : 'Nuovi Sconti Fornitore'}
                </h3>
              </div>
              <div className="sconti-forn-modal-body">

                {/* Selezione fornitore — solo in creazione */}
                {!editSf && (
                  <div className="bop-form-group">
                    <label className="bop-label">Fornitore *</label>
                    <div className="sconti-forn-cerca-fornitore-wrapper">
                      <input
                        type="text"
                        className="bop-input"
                        placeholder="Cerca fornitore..."
                        value={cercaForn}
                        onChange={e => {
                          setCercaForn(e.target.value);
                          // Se l'utente digita, deseleziona il fornitore
                          if (modalFornId) setModalFornId('');
                        }}
                      />
                      {cercaForn && fornitoriFiltrati.length > 0 && !modalFornId && (
                        <div className="sconti-forn-dropdown">
                          {fornitoriFiltrati.slice(0, 10).map(f => (
                            <div
                              key={f.id}
                              className="sconti-forn-dropdown-item"
                              onClick={() => {
                                setModalFornId(String(f.id));
                                setCercaForn(f.ragioneSociale || f.nome || '');
                              }}
                            >
                              {f.ragioneSociale || f.nome}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {modalFornId && (
                      <div className="sconti-forn-fornitore-selezionato">
                        <span>
                          ✓ {fornitori.find(f => String(f.id) === modalFornId)?.ragioneSociale ||
                             fornitori.find(f => String(f.id) === modalFornId)?.nome}
                        </span>
                        <button
                          type="button"
                          className="sconti-forn-btn-deseleziona"
                          onClick={() => { setModalFornId(''); setCercaForn(''); }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Griglia sconti 2 colonne */}
                <div className="sconti-forn-modal-grid">
                  {/* Colonna vendite */}
                  <div>
                    <h4 className="sconti-forn-col-titolo sconti-forn-col-vendite">
                      Sconti sulle vendite
                    </h4>
                    {([
                      { label: 'Sconto basso', field: 'vendBassoId' },
                      { label: 'Sconto medio', field: 'vendMedioId' },
                      { label: 'Sconto alto',  field: 'vendAltoId'  },
                      { label: 'Sconto max',   field: 'vendMaxId'   },
                    ] as const).map(({ label, field }) => (
                      <div key={field} className="bop-form-group">
                        <label className="bop-label sconti-forn-label-vendite">{label}</label>
                        <select
                          className="bop-select"
                          value={form[field]}
                          onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                        >
                          <option value="">— nessuno —</option>
                          {sconti.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.codice}
                              {s.descrizione ? ` — ${s.descrizione}` : ''}
                              {` (${Number(s.percentualeEquivalente).toFixed(2)}%)`}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  {/* Colonna acquisti */}
                  <div>
                    <h4 className="sconti-forn-col-titolo sconti-forn-col-acquisti">
                      Sconti sugli acquisti
                    </h4>
                    {([
                      { label: 'Sconto basso', field: 'acqBassoId' },
                      { label: 'Sconto medio', field: 'acqMedioId' },
                      { label: 'Sconto alto',  field: 'acqAltoId'  },
                      { label: 'Sconto max',   field: 'acqMaxId'   },
                    ] as const).map(({ label, field }) => (
                      <div key={field} className="bop-form-group">
                        <label className="bop-label sconti-forn-label-acquisti">{label}</label>
                        <select
                          className="bop-select"
                          value={form[field]}
                          onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                        >
                          <option value="">— nessuno —</option>
                          {sconti.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.codice}
                              {s.descrizione ? ` — ${s.descrizione}` : ''}
                              {` (${Number(s.percentualeEquivalente).toFixed(2)}%)`}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {errModal && (
                  <div className="articoli-modal-error">{errModal}</div>
                )}
              </div>

              <div className="sconti-forn-modal-footer">
                <button
                  className="bop-btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Annulla
                </button>
                <button
                  className="bop-btn-primary"
                  onClick={handleSalva}
                  disabled={saving}
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale ricerca articolo */}
        {showModalArticolo && (
          <div
            className="articoli-modal-overlay"
            onClick={() => setShowModalArticolo(false)}
          >
            <div
              className="sconti-forn-modal-articolo"
              onClick={e => e.stopPropagation()}
            >
              <div className="articoli-modal-header">
                <h3 className="articoli-modal-title">Cerca Articolo</h3>
                <button
                  className="articoli-modal-close"
                  onClick={() => setShowModalArticolo(false)}
                >
                  ✕
                </button>
              </div>

              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Codice / Descrizione</label>
                  <input
                    type="text"
                    className="bop-input"
                    value={modalQ}
                    onChange={e => setModalQ(e.target.value)}
                    placeholder="Cerca..."
                    autoFocus
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Tipologia</label>
                  <select
                    className="bop-select"
                    value={modalTipologia}
                    onChange={e => setModalTipologia(e.target.value)}
                  >
                    <option value="">Tutte</option>
                    <option value="ARTICOLO_FISICO">Articolo Fisico</option>
                    <option value="PRODOTTO_CONFIGURATO">Prodotto Configurato</option>
                    <option value="LAVORAZIONE_PRESTAZIONE">Lavorazione/Prestazione</option>
                    <option value="LICENZA">Licenza</option>
                    <option value="CANONE">Canone</option>
                  </select>
                </div>
              </div>

              <div className="sconti-forn-modal-risultati">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Codice</th>
                      <th>Descrizione</th>
                      <th>Tipologia</th>
                      <th>U.M.</th>
                      <th>Prezzo std</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingModal ? (
                      <tr>
                        <td colSpan={5}>
                          <div className="bop-empty-state">Caricamento...</div>
                        </td>
                      </tr>
                    ) : articoliModal.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          <div className="bop-empty-state">Nessun articolo trovato</div>
                        </td>
                      </tr>
                    ) : (
                      articoliModal.map(a => (
                        <tr
                          key={a.id}
                          className="articoli-table-row"
                          onClick={() => {
                            setArticoloSel(a);
                            setShowModalArticolo(false);
                          }}
                        >
                          <td className="articoli-codice">{a.codice}</td>
                          <td>{a.descrizioneBase}</td>
                          <td>{TIPOLOGIE_LABEL[a.tipologia] || a.tipologia}</td>
                          <td>{a.umVendita?.simbolo || '-'}</td>
                          <td className="bop-text-right">
                            {a.prezzoStandard !== null
                              ? `€ ${Number(a.prezzoStandard).toFixed(2)}`
                              : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
